/**
 * Create Content Overview Page
 * Choose which content creation tool to use
 */

import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Icon, type IconName } from '../components/icons';

interface ContentType {
  id: string;
  title: string;
  description: string;
  icon: IconName;
  gradient: string;
  route: string;
  tool: string;
  features: string[];
}

export function CreateContent() {
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();

  const contentTypes: ContentType[] = [
    {
      id: 'graphic',
      title: 'Social Graphic',
      description: 'Create stunning social media graphics with AI',
      icon: 'image',
      gradient: 'from-green-500 to-teal-500',
      route: '/create/graphic',
      tool: 'Kainva',
      features: [
        'Prompt-to-design generation',
        'Auto-sized for all platforms',
        'Brand-consistent visuals',
        'Multiple variations',
      ],
    },
    {
      id: 'reel',
      title: 'Video Reel',
      description: 'Create engaging faceless video reels with AI',
      icon: 'video',
      gradient: 'from-purple-500 to-pink-500',
      route: '/create/reel',
      tool: 'AutoReelr',
      features: [
        'AI-generated scripts',
        'Automated voiceovers',
        'Stock footage integration',
        'Brand-consistent styling',
      ],
    },
    {
      id: 'trailer',
      title: 'Video Trailer',
      description: 'Generate cinematic trailers for your products',
      icon: 'film',
      gradient: 'from-blue-500 to-cyan-500',
      route: '/create/trailer',
      tool: 'RegistaAI',
      features: [
        'Cinematic effects',
        'Dynamic transitions',
        'Music integration',
        'Professional editing',
      ],
    },
    {
      id: 'pr',
      title: 'Press Release',
      description: 'Create professional PR announcements',
      icon: 'megaphone',
      gradient: 'from-orange-500 to-red-500',
      route: '/create/pr',
      tool: 'Shoutlio',
      features: [
        'PR-optimized format',
        'Distribution ready',
        'SEO-friendly',
        'Media contact lists',
      ],
    },
  ];

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center p-8">
          <Icon name="sparkles" size="3xl" className="mx-auto mb-4 opacity-40" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Select a Brand
          </h2>
          <p className="text-gray-600 mb-4">
            Please select a brand from the dashboard to start creating content
          </p>
          <Button variant="primary" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Create Content</h1>
        <p className="text-gray-600 mt-2">
          Choose a content creation tool to get started with{' '}
          <span
            className="font-medium"
            style={{ color: selectedBrand.color_primary }}
          >
            {selectedBrand.name}
          </span>
        </p>
      </div>

      {/* AI-Powered Badge */}
      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg w-fit">
        <Icon name="sparkles" size="md" />
        <span className="text-sm font-medium text-purple-900">
          All content uses your Brand DNA for consistent voice & style
        </span>
      </div>

      {/* Content Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {contentTypes.map((type) => (
          <div
            key={type.id}
            className="cursor-pointer"
            onClick={() => navigate(type.route)}
          >
            <Card className="hover:shadow-lg transition-shadow h-full">
            <div className="p-6">
              {/* Icon with gradient */}
              <div
                className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${type.gradient} flex items-center justify-center mb-4`}
              >
                <Icon name={type.icon} size="xl" />
              </div>

              {/* Title & Description */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {type.title}
              </h3>
              <p className="text-gray-600 text-sm mb-4">{type.description}</p>

              {/* Tool Badge */}
              <div className="mb-4">
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                  via {type.tool}
                </span>
              </div>

              {/* Features */}
              <ul className="space-y-2 mb-6">
                {type.features.map((feature, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <Icon name="check" size="sm" className="mr-2 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button variant="outline" className="w-full">
                Create {type.title}
              </Button>
            </div>
            </Card>
          </div>
        ))}
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            Seamless Workflow
          </h3>
          <p className="text-blue-800">
            Create your content here, and it will automatically appear in your Posts
            section. From there, you can schedule and publish across all your
            connected social platforms.
          </p>
        </div>
      </Card>
    </div>
  );
}
